import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import {
  IErrorMessage,
  useErrorMessage
} from '@shared/hooks/use-error-message';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TabsComponent from '@shared/components/tabs/tabs';
import { GenresProps } from '@modules/entertainment/models/music.models';
import { LanguageEnum } from '@shared/models/language.model';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import useLanguageStore from '@store/language-store/language.store';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@shared/utils/trim-form-values-utils';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';

const GenreAddDialog: React.FC<GenresProps> = ({
  type,
  selectedValue,
  handleSave,
  loading,
  showLanguageTabs = true
}) => {
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({ mode: 'onChange' });

  const getErrorMessage = useErrorMessage();

  const { t } = useTranslation();
  const { languageList } = useLanguageStore((state) => state);
  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = useState<string>(defaultLang);
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );
  const [multiLangFields, setMultiLangFields] = useState<{
    name: Record<string, string | undefined>;
  }>({
    name: {}
  });

  const errorMessages: IErrorMessage = {
    name: {
      required: 'validation_name_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    }
  };

  const handleSaveGenre = async (data: any) => {
    const trimmedData = trimFormValues(data);
    const currentName = watch('name');

    const finalData = {
      ...trimmedData,
      name: { ...multiLangFields.name, [currentTab]: currentName },
      image: selectedMedia.genreImage?.path || null,
      type
    };

    handleSave(finalData);
  };

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentName = watch('name');

    setMultiLangFields((prev) => ({
      name: { ...prev.name, [currentTab]: currentName }
    }));

    setCurrentTab(newLang);
    setValue('name', multiLangFields.name?.[newLang] || '');
  };

  useEffect(() => {
    if (selectedValue) {
      const { name } = selectedValue;

      const normalizedName =
        typeof name === 'string' ? { [defaultLang]: name } : name || {};

      setMultiLangFields({ name: normalizedName });

      reset({ name: normalizedName[defaultLang] || '' });
    } else {
      reset({ name: '' });
      setMultiLangFields({ name: {} });
    }
  }, [defaultLang, selectedValue, reset]);

  return (
    <div>
      <div>
        <form className="flex flex-col gap-1">
          {showLanguageTabs && (
            <div className="w-full mb-4">
              <TabsComponent
                tabs={languageList}
                value={currentTab}
                onChange={handleChangeLanguage}
              />
            </div>
          )}

          <FormControl error={!!errors.name}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_name')}
              <InfoTooltip text="tooltip_info_genre_name" />
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: {
                  notOnlySpaces: validateNotOnlySpaces
                }
              })}
              error={!!errors.name}
            />
            <FormHelperText>
              {errors?.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages
                })}
            </FormHelperText>
          </FormControl>

          <div className="media-wrapper">
            <div className="flex mb-1.5">
              <span className="reuqired-symbole">*</span>
              {t('label_image')}
            </div>
            <MediaLibraryComponent
              mediaKey={'genreImage'}
              width={240}
              height={120}
              allowedTypes={[FileTypeEnum.Image]}
              path={selectedValue?.image}
              onSelect={(media) =>
                setSelectedMedia((prev: any) => ({
                  ...prev,
                  genreImage: media
                }))
              }
            />
          </div>

          <div className="flex justify-end items-center gap-6">
            <Button
              loading={loading}
              disabled={!isValid || !selectedMedia.genreImage}
              onClick={handleSubmit(handleSaveGenre)}
              variant="contained"
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenreAddDialog;
